import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { ContestScreeningBadge } from "@/components/contest-submissions/contest-screening-badge";
import { contestSubmissionsQueryOptions } from "@/hooks/use-contest-submissions";

import { formatSubmittedAt } from "./format";

const ENTRY_LIMIT = 3;

interface Props {
  userId: string;
  /** 현재 보고 있는 제출물 — 목록에서 하이라이트 */
  currentSubmissionId: string;
}

/**
 * 같은 응모자의 응모작 묶어보기 — 중간에 또 낸 사람의 편수(3편 제한) 확인용.
 * 서버 목록 쿼리의 userId 필터 재사용.
 */
export function SubmitterSubmissions({ userId, currentSubmissionId }: Props) {
  const query = useQuery(
    contestSubmissionsQueryOptions({ userId }, { page: 1, limit: 10 }),
  );

  if (query.isPending) {
    return <p className="text-sm text-muted-foreground">불러오는 중…</p>;
  }
  if (query.isError || !query.data) {
    return (
      <p className="text-sm text-muted-foreground">
        응모자 제출 이력을 불러오지 못했습니다.
      </p>
    );
  }

  const { items, meta } = query.data;
  const overLimit = meta.totalCount > ENTRY_LIMIT;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-sm">
          총 <span className="font-bold tabular-nums">{meta.totalCount}</span>편 응모
        </span>
        {overLimit && (
          <Badge
            variant="outline"
            className="gap-1 border-rose-200 bg-rose-50 text-rose-700"
          >
            <AlertTriangle className="h-3 w-3" />
            편수 초과 ({ENTRY_LIMIT}편 제한)
          </Badge>
        )}
      </div>
      <div className="divide-y rounded-md border">
        {items.map((item) => {
          const isCurrent = item._id === currentSubmissionId;
          return (
            <div
              key={item._id}
              className={
                isCurrent
                  ? "flex items-center gap-3 bg-muted/60 px-3 py-2"
                  : "flex items-center gap-3 px-3 py-2"
              }
            >
              <div className="min-w-0 flex-1">
                {isCurrent ? (
                  <span className="text-sm font-medium">
                    {item.workTitle || "(제목 없음)"}
                    <span className="ml-1.5 text-xs text-muted-foreground">
                      (현재 보는 중)
                    </span>
                  </span>
                ) : (
                  <Link
                    to={`/contest-submissions/${item._id}`}
                    className="text-sm font-medium text-primary underline-offset-2 hover:underline"
                  >
                    {item.workTitle || "(제목 없음)"}
                  </Link>
                )}
                <p className="text-xs text-muted-foreground">
                  {formatSubmittedAt(item)} 제출
                </p>
              </div>
              <ContestScreeningBadge screening={item.screening} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
